import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { useSafetyComplianceStore } from "../../../store/hazardousMaterialStore";
import { SafetyComplianceType } from "../../../types/api";
// Add these imports
import { useS3Upload, S3TempUrl } from "../../../utils/s3Client";
import FileUpload from "../../../components/FileUpload/FileUpload";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgba(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

// Update emptyForm to include attachments array
const emptyForm: SafetyComplianceType = {
  complianceDocumentId: "",
  regulationName: "",
  certificationBody: "",
  complianceStatus: "",
  certificationExpiryDate: "",
  lastInspectionDate: "",
  nextInspectionDate: "",
  inspectorName: "",
  auditFindingsSummary: "",
  correctiveActionsRequired: false,
  correctiveActionDetails: "",
  equipmentSafetyChecks: "",
  safetyTrainingCompliance: "",
  emergencyPreparednessMeasures: "",
  infectionControlAdherence: "",
  staffCertificationsVerified: false,
  highestQualificationRecorded: "",
  ongoingTraining: "",
  numberOfSafetyIncidents: "",
  incidentDescriptions: "",
  followUpActions: "",
  remarks: "",
  attachments: [], // Add attachments array
};

const complianceStatusOptions = ["Compliant", "Pending", "Non-Compliant"];

// Define Attachment interface
interface Attachment {
  url: string;
  filename?: string;
  name?: string;
  originalName?: string;
  uploadedAt?: string;
}

const SafetyAndComplianceInfo: React.FC = () => {
  const [rowData, setRowData] = useState<SafetyComplianceType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<SafetyComplianceType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  // Add file upload states
  const [files, setFiles] = useState<File[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    []
  );
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");
  const [maxFileSize] = useState<number>(10);

  // Document preview states
  const [fileurl, setFileUrl] = useState({
    url: "",
    name: "",
    type: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);

  // Initialize hooks for file handling
  const { getsignedurl } = S3TempUrl();
  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  const {
    safetyComplianceRecords,
    loading,
    fetchAllSafetyCompliance,
    createSafetyCompliance,
    updateSafetyCompliance,
    deleteSafetyCompliance,
  } = useSafetyComplianceStore();

  useEffect(() => {
    fetchAllSafetyCompliance();
  }, [fetchAllSafetyCompliance]);

  useEffect(() => {
    setRowData(safetyComplianceRecords ?? []);
  }, [safetyComplianceRecords]);

  // Helper function to get file extension
  const getFileExtension = (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  // Helper function to check if file can be previewed in browser
  const canPreviewFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return [
      "jpg",
      "jpeg",
      "png",
      "pdf",
      "txt",
      "doc",
      "docx",
      "ppt",
      "pptx",
    ].includes(ext);
  };

  // Function to handle file preview
  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split("/").pop() ||
      "";

    if (canPreviewFile(filename)) {
      try {
        setIsLoading(true);

        const s3Key = attachment.url?.split("/").pop();
        if (!s3Key) {
          throw new Error("Invalid file URL");
        }

        const fileurl = await getsignedurl(s3Key, getFileExtension(filename));
        const filobj = {
          url: fileurl,
          name: filename,
          type: getFileExtension(filename),
        };
        setFileUrl(filobj);
        setIsDocumentViewerOpen(true);
      } catch (err: any) {
        console.error("Error previewing file:", err);
        setErrorMessage("File not found or failed to load");
        toast.error("Failed to preview file");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.info(
        "Preview not available for this file type. Use download instead."
      );
    }
  };

  // Function to close document viewer
  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
    setErrorMessage("");
  };

  // Function to remove existing attachment (for update mode)
  const handleRemoveExistingAttachment = (index: number) => {
    if (isEditMode) {
      setExistingAttachments((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target as any;
    if (type === "checkbox") {
      setFormData((prev: SafetyComplianceType) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setFormData((prev: SafetyComplianceType) => ({ ...prev, [name]: value }));
    }
  };

  // File upload handlers
  const handleFileChange = async (acceptedFiles: File[]) => {
    const maxTotalSize = maxFileSize * 1024 * 1024; // 10MB total limit in bytes
    const existingFiles = files || [];
    const existingTotalSize = existingFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );

    // Calculate total size if new files are added
    const newFilesTotalSize = acceptedFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );
    const totalSize = existingTotalSize + newFilesTotalSize;

    if (totalSize > maxTotalSize) {
      toast.error(`Total file size exceeds ${maxFileSize}MB limit.`);
      return;
    }

    // Store files temporarily - they will be uploaded on submit
    const updatedFiles = [...existingFiles, ...acceptedFiles];
    handleFilesChange(updatedFiles);
  };

  const handleFilesChange = (newFiles: File[] | null) => {
    setFiles(newFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileChange,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    disabled: submitting || uploading,
    multiple: true,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Basic validation
    const missingFields: string[] = [];
    if (!formData.complianceDocumentId)
      missingFields.push("Compliance Document ID");
    if (!formData.regulationName) missingFields.push("Regulation Name");
    if (!formData.complianceStatus) missingFields.push("Compliance Status");

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    // Validate total file size (10MB limit)
    if (files && files.length > 0) {
      const maxTotalSize = maxFileSize * 1024 * 1024; // 10MB total limit in bytes
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);

      if (totalSize > maxTotalSize) {
        toast.error(
          `Total file size exceeds ${maxFileSize}MB limit. Please remove some files.`
        );
        setUploadErrorMessage(
          "Total file size exceeds 10MB limit. Please remove some files."
        );
        return;
      }
    }

    setSubmitting(true);
    setUploadErrorMessage("");

    let uploadedFileObjects: Array<{
      url: string;
      name: string;
      uploadedAt: string;
    }> = [];

    try {
      if (files && files.length > 0) {
        for (const file of files) {
          const s3Url = await uploadFileToS3(file);
          if (s3Url) {
            uploadedFileObjects.push({
              url: s3Url,
              name: file.name,
              uploadedAt: new Date().toISOString(),
            });
          } else {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }
    } catch (error) {
      setUploadErrorMessage(`File upload failed: ${(error as Error).message}`);
      setSubmitting(false);
      return;
    }

    try {
      // Prepare existing attachments as file objects
      const existingAttachmentObjects = existingAttachments.map((a) => ({
        url: a.url || (typeof a === "string" ? a : ""),
        name:
          a.filename ||
          a.name ||
          a.originalName ||
          a.url?.split("/").pop() ||
          "Unknown file",
        uploadedAt: a.uploadedAt || new Date().toISOString(),
      }));

      const payload = {
        ...formData,
        attachments: [...existingAttachmentObjects, ...uploadedFileObjects],
      };

      let success;
      if (isEditMode && editId) {
        success = await updateSafetyCompliance(payload, editId);
      } else {
        success = await createSafetyCompliance(payload);
      }

      if (success) {
        const successMessage = isEditMode
          ? "Safety & compliance record updated successfully"
          : "Safety & compliance record created successfully";
        toast.success(successMessage);

        setModalOpen(false);
        setFormData(emptyForm);
        setEditId(null);
        setIsEditMode(false);
        setFiles(null);
        setExistingAttachments([]);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        (isEditMode
          ? "Failed to update safety & compliance record"
          : "Failed to create safety & compliance record");
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (data: SafetyComplianceType) => {
    setIsEditMode(true);

    // Extract attachments from data if they exist
    let attachmentsArray: Attachment[] = [];
    if (data.attachments && Array.isArray(data.attachments)) {
      attachmentsArray = data.attachments.map((attachment: any) => {
        if (typeof attachment === "string") {
          return {
            url: attachment,
            filename: attachment.split("/").pop() || "Unknown file",
            uploadedAt: new Date().toISOString(),
          };
        } else {
          return {
            url: attachment.url || attachment,
            filename:
              attachment.filename ||
              attachment.name ||
              attachment.originalName ||
              attachment.url?.split("/").pop() ||
              "Unknown file",
            uploadedAt: attachment.uploadedAt || new Date().toISOString(),
          };
        }
      });
    }

    setFormData({
      ...data,
    });

    setExistingAttachments(attachmentsArray);
    setEditId(data._id || "");
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this safety & compliance record?"
    );
    if (confirmed) {
      await deleteSafetyCompliance(id);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData(emptyForm);
    setEditId(null);
    setIsEditMode(false);
    setFiles(null);
    setExistingAttachments([]);
  };

  const isDisabled = submitting || uploading;

  const columnDefs = useMemo<any[]>(
    () => [
      {
        field: "complianceDocumentId",
        headerName: "Document ID",
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px", textAlign: "left" },
      },
      {
        field: "regulationName",
        headerName: "Regulation Name",
        minWidth: 140,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "complianceStatus",
        headerName: "Status",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "certificationExpiryDate",
        headerName: "Expiry Date",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "lastInspectionDate",
        headerName: "Last Inspection",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "correctiveActionsRequired",
        headerName: "Corrective Actions",
        minWidth: 130,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (params: any) => (params.value ? "Yes" : "No"),
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Safety & Compliance Record"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Safety & Compliance Record"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const defaultColDef = useMemo<any>(
    () => ({
      editable: false,
    }),
    []
  );

  const theme = useMemo<any>(() => gridTheme, []);

  return (
    <>
      {/* Add loading overlay */}
      {(uploading || submitting) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-teal-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">
              {uploading ? "Uploading files..." : "Submitting..."}
            </h3>
            {uploading && (
              <p className="text-sm text-gray-600">
                Progress: {uploadProgress}%
              </p>
            )}
          </div>
        </div>
      )}

      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Safety & Compliance Info</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
              setExistingAttachments([]);
              setFiles(null);
            }}
          >
            <Plus size={20} /> Add Record
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No safety & compliance records added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeButton={false}
        modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode
              ? "Update Safety & Compliance"
              : "Add Safety & Compliance"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="safetyComplianceForm"
            className="flex flex-col gap-6 pb-20"
          >
            {/* Compliance Document ID */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="complianceDocumentId"
                className="text-sm font-medium text-gray-700"
              >
                Compliance Document ID<span className="text-red-500">*</span>
              </label>
              <input
                id="complianceDocumentId"
                name="complianceDocumentId"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.complianceDocumentId}
                onChange={handleChange}
                placeholder="Enter document or reference ID"
                aria-label="Compliance Document ID"
                required
                disabled={isDisabled}
              />
            </div>

            {/* Regulation Name */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="regulationName"
                className="text-sm font-medium text-gray-700"
              >
                Regulation / Standard Name
                <span className="text-red-500">*</span>
              </label>
              <input
                id="regulationName"
                name="regulationName"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.regulationName}
                onChange={handleChange}
                placeholder="e.g. OSHA, HIPAA, JCI"
                aria-label="Regulation Name"
                required
                disabled={isDisabled}
              />
            </div>

            {/* Certification Body */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="certificationBody"
                className="text-sm font-medium text-gray-700"
              >
                Certification Body
              </label>
              <input
                id="certificationBody"
                name="certificationBody"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.certificationBody}
                onChange={handleChange}
                placeholder="Issuing authority"
                aria-label="Certification Body"
                disabled={isDisabled}
              />
            </div>

            {/* Compliance Status */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="complianceStatus"
                className="text-sm font-medium text-gray-700"
              >
                Compliance Status<span className="text-red-500">*</span>
              </label>
              <select
                id="complianceStatus"
                name="complianceStatus"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.complianceStatus}
                onChange={handleChange}
                aria-label="Compliance Status"
                required
                disabled={isDisabled}
              >
                <option value="">Select Status</option>
                {complianceStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Certification Expiry Date */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="certificationExpiryDate"
                className="text-sm font-medium text-gray-700"
              >
                Certification Expiry Date
              </label>
              <input
                id="certificationExpiryDate"
                name="certificationExpiryDate"
                type="date"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.certificationExpiryDate}
                onChange={handleChange}
                aria-label="Certification Expiry Date"
                disabled={isDisabled}
              />
            </div>

            {/* Last Inspection Date */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="lastInspectionDate"
                className="text-sm font-medium text-gray-700"
              >
                Last Inspection Date
              </label>
              <input
                id="lastInspectionDate"
                name="lastInspectionDate"
                type="date"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.lastInspectionDate}
                onChange={handleChange}
                aria-label="Last Inspection Date"
                disabled={isDisabled}
              />
            </div>

            {/* Next Inspection Date */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="nextInspectionDate"
                className="text-sm font-medium text-gray-700"
              >
                Next Scheduled Inspection Date
              </label>
              <input
                id="nextInspectionDate"
                name="nextInspectionDate"
                type="date"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.nextInspectionDate}
                onChange={handleChange}
                aria-label="Next Scheduled Inspection Date"
                disabled={isDisabled}
              />
            </div>

            {/* Inspector Name */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="inspectorName"
                className="text-sm font-medium text-gray-700"
              >
                Inspector Name
              </label>
              <input
                id="inspectorName"
                name="inspectorName"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.inspectorName}
                onChange={handleChange}
                placeholder="Name of inspector"
                aria-label="Inspector Name"
                disabled={isDisabled}
              />
            </div>

            {/* Audit Findings Summary */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="auditFindingsSummary"
                className="text-sm font-medium text-gray-700"
              >
                Audit Findings Summary
              </label>
              <textarea
                id="auditFindingsSummary"
                name="auditFindingsSummary"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.auditFindingsSummary}
                onChange={handleChange}
                placeholder="Summary of audit findings"
                aria-label="Audit Findings Summary"
                disabled={isDisabled}
              />
            </div>

            {/* Corrective Actions Required */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="correctiveActionsRequired"
                name="correctiveActionsRequired"
                checked={formData.correctiveActionsRequired}
                onChange={handleChange}
                className="form-checkbox"
                disabled={isDisabled}
              />
              <label
                htmlFor="correctiveActionsRequired"
                className="text-sm font-medium text-gray-700"
              >
                Corrective Actions Required
              </label>
            </div>

            {/* Corrective Action Details */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="correctiveActionDetails"
                className="text-sm font-medium text-gray-700"
              >
                Corrective Action Details
              </label>
              <textarea
                id="correctiveActionDetails"
                name="correctiveActionDetails"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.correctiveActionDetails}
                onChange={handleChange}
                placeholder="Details of required actions, if any"
                aria-label="Corrective Action Details"
                disabled={isDisabled}
              />
            </div>

            {/* Equipment Safety Checks */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="equipmentSafetyChecks"
                className="text-sm font-medium text-gray-700"
              >
                Equipment Safety Checks
              </label>
              <textarea
                id="equipmentSafetyChecks"
                name="equipmentSafetyChecks"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.equipmentSafetyChecks}
                onChange={handleChange}
                placeholder="Status or dates of equipment safety checks"
                aria-label="Equipment Safety Checks"
                disabled={isDisabled}
              />
            </div>

            {/* Safety Training Compliance */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="safetyTrainingCompliance"
                className="text-sm font-medium text-gray-700"
              >
                Safety Training Compliance
              </label>
              <input
                id="safetyTrainingCompliance"
                name="safetyTrainingCompliance"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.safetyTrainingCompliance}
                onChange={handleChange}
                placeholder="e.g., percentage completed or status"
                aria-label="Safety Training Compliance"
                disabled={isDisabled}
              />
            </div>

            {/* Emergency Preparedness Measures */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="emergencyPreparednessMeasures"
                className="text-sm font-medium text-gray-700"
              >
                Emergency Preparedness Measures
              </label>
              <textarea
                id="emergencyPreparednessMeasures"
                name="emergencyPreparednessMeasures"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.emergencyPreparednessMeasures}
                onChange={handleChange}
                placeholder="Describe emergency readiness"
                aria-label="Emergency Preparedness Measures"
                disabled={isDisabled}
              />
            </div>

            {/* Infection Control Protocols Adherence */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="infectionControlAdherence"
                className="text-sm font-medium text-gray-700"
              >
                Infection Control Protocols Adherence
              </label>
              <textarea
                id="infectionControlAdherence"
                name="infectionControlAdherence"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.infectionControlAdherence}
                onChange={handleChange}
                placeholder="Details on infection control compliance"
                aria-label="Infection Control Protocols Adherence"
                disabled={isDisabled}
              />
            </div>

            {/* Staff Certifications Verified */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="staffCertificationsVerified"
                name="staffCertificationsVerified"
                checked={formData.staffCertificationsVerified}
                onChange={handleChange}
                className="form-checkbox"
                disabled={isDisabled}
              />
              <label
                htmlFor="staffCertificationsVerified"
                className="text-sm font-medium text-gray-700"
              >
                Staff Certifications Verified
              </label>
            </div>

            {/* Highest Qualification Recorded */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="highestQualificationRecorded"
                className="text-sm font-medium text-gray-700"
              >
                Highest Qualification Recorded
              </label>
              <input
                id="highestQualificationRecorded"
                name="highestQualificationRecorded"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.highestQualificationRecorded}
                onChange={handleChange}
                placeholder="Qualification level"
                aria-label="Highest Qualification Recorded"
                disabled={isDisabled}
              />
            </div>

            {/* Ongoing Training and Education */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="ongoingTraining"
                className="text-sm font-medium text-gray-700"
              >
                Ongoing Training & Education
              </label>
              <textarea
                id="ongoingTraining"
                name="ongoingTraining"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.ongoingTraining}
                onChange={handleChange}
                placeholder="Details about ongoing staff training"
                aria-label="Ongoing Training and Education"
                disabled={isDisabled}
              />
            </div>

            {/* Number of Safety Incidents */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="numberOfSafetyIncidents"
                className="text-sm font-medium text-gray-700"
              >
                Number of Safety Incidents (Period)
              </label>
              <input
                id="numberOfSafetyIncidents"
                name="numberOfSafetyIncidents"
                type="number"
                min={0}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.numberOfSafetyIncidents}
                onChange={handleChange}
                placeholder="Total incidents recorded"
                aria-label="Number of Safety Incidents"
                disabled={isDisabled}
              />
            </div>

            {/* Incident Descriptions */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="incidentDescriptions"
                className="text-sm font-medium text-gray-700"
              >
                Incident Descriptions
              </label>
              <textarea
                id="incidentDescriptions"
                name="incidentDescriptions"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.incidentDescriptions}
                onChange={handleChange}
                placeholder="Summary or details of incidents"
                aria-label="Incident Descriptions"
                disabled={isDisabled}
              />
            </div>

            {/* Follow-up Actions */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="followUpActions"
                className="text-sm font-medium text-gray-700"
              >
                Follow-up Actions Taken
              </label>
              <textarea
                id="followUpActions"
                name="followUpActions"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.followUpActions}
                onChange={handleChange}
                placeholder="Actions after incidents or audits"
                aria-label="Follow-up Actions"
                disabled={isDisabled}
              />
            </div>

            {/* Remarks */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="remarks"
                className="text-sm font-medium text-gray-700"
              >
                Remarks
              </label>
              <textarea
                id="remarks"
                name="remarks"
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Additional notes"
                aria-label="Remarks"
                disabled={isDisabled}
              />
            </div>

            {/* File Upload Section - Add this */}
            <FileUpload
              mode={isEditMode ? "update" : "create"}
              files={files}
              onFilesChange={handleFilesChange}
              existingAttachments={existingAttachments}
              onRemoveExistingAttachment={handleRemoveExistingAttachment}
              onPreviewFile={handlePreviewFile}
              uploading={uploading}
              uploadProgress={uploadProgress}
              disabled={isDisabled}
              maxFileSizeMB={maxFileSize}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
            />

            <div className="h-20" />
          </form>
        </div>

        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={handleModalClose}
            disabled={isDisabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="safetyComplianceForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
            disabled={isDisabled}
          >
            {isDisabled ? "Submitting..." : isEditMode ? "Update" : "Add"}
          </button>
        </div>
      </Modal>

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-45">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
            <div className="absolute right-4 top-4 z-10">
              <button
                onClick={handleCloseDocumentViewer}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Close document viewer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {errorMessage}
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : fileurl.url ? (
                fileurl.type === "pdf" ? (
                  <div className="w-full h-full">
                    <PdfViewer fileUrl={fileurl.url} />
                  </div>
                ) : (
                  <div className="w-full h-full">
                    <DocumentViewer file={fileurl.url} name={fileurl.name} />
                  </div>
                )
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p>No document to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SafetyAndComplianceInfo;
